/**
 * Cópia LITERAL do regex que o `z.string().uuid()` do core aplica — Zod 4.4.3,
 * `node_modules/zod/v4/core/regexes.js:22-24`: versão **1–8**, variante `[89ab]`,
 * mais o nil e o max UUID como alternativas explícitas.
 *
 * A versão anterior desta constante exigia `[1-5]` e o JSDoc afirmava ser "a
 * mesma régua do core". Não era: um id fora de v1–v5 devolveria `false`, o alvo
 * iria por `search=` e seria comparado contra `numeroProcesso` — que nunca casa
 * com um UUID — e o operador veria "Não foi possível abrir este caso" para um
 * caso que existe. Hoje o id de processo é `gen_random_uuid()` (v4), então a
 * divergência ainda não era alcançável; ela passaria a ser no dia em que o
 * gerador mudar, sem nada quebrar por perto que avisasse.
 */
const UUID_RFC9562 =
  /^([0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}|00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)$/i;

/** Só a FORMA 8-4-4-4-12, sem cobrar versão nem variante (o `z.guid()` do Zod). */
const FORMA_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Decide se um identificador vindo da URL é id de recurso ou texto de busca:
 * mandar um número de processo num param tipado como UUID faz o Zod do core
 * recusar a query inteira.
 *
 * O papel aqui é ROTEAMENTO entre dois params, não validação. Ser mais frouxo
 * que o servidor é inofensivo (o pior caso é o core devolver 0 linhas, que já
 * tem tratamento); ser mais estrito é o único jeito de errar — por isso a régua
 * é a cópia da do core, e não uma mais apertada.
 */
export const ehUuid = (valor: string): boolean => UUID_RFC9562.test(valor);

/**
 * Verdadeiro para qualquer string com a forma de UUID, sem cobrar versão.
 *
 * Régua deliberadamente mais frouxa que `ehUuid`: quem pergunta isto está
 * decidindo se um valor é identificador TÉCNICO (para escondê-lo de uma tela),
 * e aí um id com versão não-canônica continua sendo ruído para o leitor.
 */
export const temFormaDeUuid = (valor: string): boolean => FORMA_UUID.test(valor);

export const generateUuid = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  const bytes = Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const toHex = (value: number) => value.toString(16).padStart(2, '0');
  const hex = bytes.map(toHex).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20,
  )}-${hex.slice(20)}`;
};
